import { Webinar } from "rainbow-web-sdk/models/webinar.model";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { WebinarServiceRB } from "rainbow-web-sdk/services/webinars/webinars.service";

export abstract class AbstractWebinarSpeakersService {
    public abstract get(webinar: Webinar, id: string): any;
    public abstract invite(webinar: Webinar, contactId: string[], emails: string[]): Promise<void>;
    public abstract remove(webinar: Webinar, contactId: string[], emails: string[]): Promise<void>;
    public abstract promoteSpeakers(webinar: Webinar, users: string[]): Promise<void>;
    public abstract demoteOrganizers(webinar: Webinar, users: string[]): Promise<void>;
    public abstract updateOrganizer(webinar: Webinar, user: string, hightlighted: boolean): Promise<void>;
}

export class WebinarSpeakersService extends AbstractWebinarSpeakersService {

    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    public static create(webinarService: WebinarServiceRB): WebinarSpeakersService {
        return new WebinarSpeakersService(webinarService);
    }

    constructor(private webinarService: WebinarServiceRB) {
        super();
    }

    public get(webinar: Webinar, id: string): any {
        const allSpeakers = webinar.organizers.concat(webinar.speakers);
        return allSpeakers.find((speaker: any) => { return speaker.userId === id; });
    }

    public async invite(webinar: Webinar, users: string[], emails: string[]): Promise<any> {
        try {
            this.logger.info(`[WebinarServiceSpeakers] inviteSpeakers (web: ${webinar.id})`);

            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/invitations/moderators`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ users, emails });
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;

            // handle positive case
            const jsonResponse = await response.json();
            const data = jsonResponse.data;
            webinar.updateFromData(data.webinar);
            this.webinarService.updateRoles(webinar);
            webinar.rxSubject.next(null);

            // Handle invitation failure
            const failed = jsonResponse.failed;
            if (failed?.invalidUsers && failed.invalidUsers.length > 0) {
                return failed.invalidUsers.map((invalidUser: any) => {
                    return { id: invalidUser.id, errorCode: invalidUser.errorDetailsCode };
                });
            }
            return null;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `inviteSpeakers (web: ${webinar.id})`);
            this.logger.error(`[WebinarServiceSpeakers] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async remove(webinar: Webinar, users: string[], emails: string[]) {
        try {
            this.logger.info(`[WebinarServiceSpeakers] removeSpeakers (web: ${webinar.id})`);

            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/moderators/remove`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ users, emails });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data.webinar);
            webinar.rxSubject.next(null);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `removeSpeakers (web: ${webinar.id})`);
            this.logger.error(`[WebinarServiceSpeakers] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async promoteSpeakers(webinar: Webinar, users: string[]): Promise<void> {
        try {
            this.logger.info(`[WebinarServiceSpeakers] promoteSpeakers (web: ${webinar.id})`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/moderators/promote`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ users });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            this.webinarService.updateRoles(webinar);
            webinar.rxSubject.next(null);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `promoteSpeakers (web: ${webinar.id})`);
            this.logger.error(`[WebinarServiceSpeakers] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async demoteOrganizers(webinar: Webinar, users: string[]): Promise<void> {
        try {
            this.logger.info(`[WebinarServiceSpeakers] demoteOrganizers (web: ${webinar.id})`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/moderators/demote`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ users });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            this.webinarService.updateRoles(webinar);
            webinar.rxSubject.next(null);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `demoteOrganizers (web: ${webinar.id})`);
            this.logger.error(`[WebinarServiceSpeakers] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateOrganizer(webinar: Webinar, userId: string, hightlighted: boolean): Promise<void> {
        try {
            this.logger.info(`[WebinarServiceSpeakers] updateOrganizer (web: ${webinar.id}, part: ${userId})`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/moderators/${userId}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ hightlighted });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            this.webinarService.updateRoles(webinar);
            webinar.rxSubject.next(null);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `updateOrganizer (web: ${webinar.id}, part: ${userId})`);
            this.logger.error(`[WebinarServiceSpeakers] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

}
